// src/pages/PublicNfcCard.js
//
// Public-facing digital business card. Lives at /card/:slug, OUTSIDE the
// app shell so it's accessible without logging in (clients scan an NFC card
// or open the URL on their phone).
//
// The lead-capture flow: client fills name/surname/company → tapping the
// WhatsApp button opens wa.me with a pre-filled message including those
// details, so the rep instantly knows who reached out.

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ── Card-image renderer ────────────────────────────────────────────────────
// Draws a portrait business-card image (1080×1620) on a canvas so the client
// can save it to their photo gallery. We render on the client (no server round
// trip) so the download stays snappy even on mobile networks.

const CARD_W = 1080;
const CARD_H = 1620;

// Load a same-origin blob URL for the rep's photo so the canvas doesn't get
// tainted by the cross-origin API host.
async function loadRepPhoto(slug) {
  try {
    const res = await fetch(`${API_URL}/api/nfc-cards/public/${encodeURIComponent(slug)}/photo`);
    if (!res.ok) return null;
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = objectUrl;
    });
    return { img, objectUrl };
  } catch (_) {
    return null;
  }
}

// Word-wrap helper — draws lines top-to-bottom, returns the next-y cursor.
function drawWrapped(ctx, text, x, y, maxWidth, lineHeight, maxLines) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let current = '';
  for (const w of words) {
    const candidate = current ? `${current} ${w}` : w;
    if (ctx.measureText(candidate).width > maxWidth && current) {
      lines.push(current);
      current = w;
    } else {
      current = candidate;
    }
    if (maxLines && lines.length >= maxLines) break;
  }
  if (current && (!maxLines || lines.length < maxLines)) lines.push(current);
  const shown = maxLines ? lines.slice(0, maxLines) : lines;
  shown.forEach((line, i) => ctx.fillText(line, x, y + i * lineHeight));
  return y + shown.length * lineHeight;
}

async function renderCardImage(card, publicUrl, slug) {
  const canvas = document.createElement('canvas');
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext('2d');

  // ── Background: dark green gradient ─────────────────────────────────────
  const bg = ctx.createLinearGradient(0, 0, 0, CARD_H);
  bg.addColorStop(0, '#1d8c3a');
  bg.addColorStop(0.35, '#154a20');
  bg.addColorStop(1, '#0c1c10');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // ── Header brand strip ──────────────────────────────────────────────────
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.font = 'bold 44px "Inter","Helvetica Neue",Arial,sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('PAR', CARD_W / 2, 100);
  ctx.font = '500 26px "Inter","Helvetica Neue",Arial,sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.65)';
  ctx.fillText('Digital business card', CARD_W / 2, 140);

  // ── Avatar (circle) ─────────────────────────────────────────────────────
  const avatarR = 200;
  const avatarCx = CARD_W / 2;
  const avatarCy = 400;

  const photo = await loadRepPhoto(slug);

  // Ring / plate behind the avatar.
  ctx.beginPath();
  ctx.arc(avatarCx, avatarCy, avatarR + 10, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.95)';
  ctx.fill();

  ctx.save();
  ctx.beginPath();
  ctx.arc(avatarCx, avatarCy, avatarR, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  if (photo && photo.img) {
    // Cover-fit the source image inside the circle.
    const s = photo.img;
    const scale = Math.max((avatarR * 2) / s.width, (avatarR * 2) / s.height);
    const dw = s.width * scale;
    const dh = s.height * scale;
    const dx = avatarCx - dw / 2;
    const dy = avatarCy - dh / 2;
    ctx.drawImage(s, dx, dy, dw, dh);
  } else {
    // Initials fallback.
    ctx.fillStyle = '#2fb65c';
    ctx.fillRect(avatarCx - avatarR, avatarCy - avatarR, avatarR * 2, avatarR * 2);
    ctx.fillStyle = 'white';
    ctx.font = 'bold 180px "Inter",Arial,sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText((card.displayName || '?').trim().charAt(0).toUpperCase(), avatarCx, avatarCy);
    ctx.textBaseline = 'alphabetic';
  }
  ctx.restore();
  if (photo && photo.objectUrl) URL.revokeObjectURL(photo.objectUrl);

  // ── Name + headline + company block ─────────────────────────────────────
  ctx.textAlign = 'center';
  ctx.fillStyle = '#ffffff';
  ctx.font = '700 58px "Inter","Helvetica Neue",Arial,sans-serif';
  let y = 730;
  y = drawWrapped(ctx, card.displayName || 'Sales Rep', CARD_W / 2, y, CARD_W - 120, 66, 2) + 12;

  if (card.headline) {
    ctx.fillStyle = 'rgba(255,255,255,0.82)';
    ctx.font = '500 30px "Inter","Helvetica Neue",Arial,sans-serif';
    y = drawWrapped(ctx, card.headline, CARD_W / 2, y, CARD_W - 140, 40, 2) + 6;
  }
  if (card.company) {
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.font = '400 28px "Inter","Helvetica Neue",Arial,sans-serif';
    y = drawWrapped(ctx, card.company, CARD_W / 2, y, CARD_W - 140, 36, 1) + 20;
  }

  // ── Contact card (rounded panel) ────────────────────────────────────────
  const panelX = 80;
  const panelY = Math.max(y + 20, 1050);
  const panelW = CARD_W - 160;
  const panelH = 380;
  const panelR = 28;

  ctx.fillStyle = 'rgba(255,255,255,0.94)';
  ctx.beginPath();
  ctx.moveTo(panelX + panelR, panelY);
  ctx.arcTo(panelX + panelW, panelY, panelX + panelW, panelY + panelH, panelR);
  ctx.arcTo(panelX + panelW, panelY + panelH, panelX, panelY + panelH, panelR);
  ctx.arcTo(panelX, panelY + panelH, panelX, panelY, panelR);
  ctx.arcTo(panelX, panelY, panelX + panelW, panelY, panelR);
  ctx.closePath();
  ctx.fill();

  // Contact rows.
  ctx.textAlign = 'left';
  ctx.fillStyle = '#1E1E1E';
  let rowY = panelY + 80;

  if (card.whatsappNumber) {
    ctx.font = '600 26px "Inter",Arial,sans-serif';
    ctx.fillStyle = '#2fb65c';
    ctx.fillText('WHATSAPP', panelX + 50, rowY);
    ctx.font = '600 40px "Inter",Arial,sans-serif';
    ctx.fillStyle = '#1E1E1E';
    ctx.fillText(`+${card.whatsappNumber}`, panelX + 50, rowY + 50);
    rowY += 130;
  }
  if (card.email) {
    ctx.font = '600 26px "Inter",Arial,sans-serif';
    ctx.fillStyle = '#2fb65c';
    ctx.fillText('EMAIL', panelX + 50, rowY);
    ctx.font = '500 32px "Inter",Arial,sans-serif';
    ctx.fillStyle = '#1E1E1E';
    ctx.fillText(card.email, panelX + 50, rowY + 45);
    rowY += 120;
  }

  // ── Footer URL ──────────────────────────────────────────────────────────
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(255,255,255,0.65)';
  ctx.font = '500 26px "Inter",Arial,sans-serif';
  ctx.fillText('Open again at', CARD_W / 2, CARD_H - 100);
  ctx.fillStyle = 'rgba(255,255,255,0.95)';
  ctx.font = '600 28px "Inter",monospace';
  ctx.fillText(publicUrl, CARD_W / 2, CARD_H - 60);

  return await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92)
  );
}

function safeFilename(name) {
  return (String(name || 'business-card')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'business-card') + '.jpg';
}

const PublicNfcCard = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [card, setCard] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [form, setForm] = useState({ name: '', surname: '', company: '' });
  const [savingCard, setSavingCard] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/nfc-cards/public/${encodeURIComponent(slug)}`);
        if (cancelled) return;
        setCard(data);
      } catch (err) {
        if (cancelled) return;
        if (err.response && err.response.status === 404) {
          setNotFound(true);
        } else {
          console.error('Failed to load card:', err);
          setNotFound(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const canSubmit = form.name.trim().length > 0 && form.surname.trim().length > 0;

  // Build the WA message that gets pre-loaded into wa.me. Keeps the company
  // clause out if the client left it blank.
  const message = useMemo(() => {
    if (!card) return '';
    const repName = card.displayName || 'there';
    const fullName = `${form.name.trim()} ${form.surname.trim()}`.trim();
    const companyClause = form.company.trim()
      ? ` from ${form.company.trim()}`
      : '';
    return `Hi ${repName}, I'm ${fullName}${companyClause}. I scanned your card and would like to chat.`;
  }, [card, form]);

  const whatsappHref = useMemo(() => {
    if (!card || !card.whatsappNumber) return null;
    const digits = String(card.whatsappNumber).replace(/\D+/g, '');
    if (!digits) return null;
    return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
  }, [card, message]);

  // Build the client-side card image and either share it via the OS share
  // sheet (best on mobile — the user picks "Save to Photos" / "Save Image")
  // or fall back to a direct download.
  const handleSaveCard = async () => {
    if (!card || savingCard) return;
    setSavingCard(true);
    setSaveMsg(null);
    try {
      const publicUrl = `${window.location.origin}/card/${slug}`;
      const blob = await renderCardImage(card, publicUrl, slug);
      if (!blob) throw new Error('Failed to render card image');

      const filename = safeFilename(card.displayName || slug);
      const file = new File([blob], filename, { type: 'image/jpeg' });

      // Preferred path on iOS/Android: share sheet with "Save Image".
      const canShareFiles =
        typeof navigator !== 'undefined' &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [file] });

      if (canShareFiles) {
        try {
          await navigator.share({
            files: [file],
            title: `${card.displayName || 'Business card'}`,
            text: card.displayName || 'Business card'
          });
          setSaveMsg('Card saved.');
          return;
        } catch (err) {
          // User cancelled — no need to show an error, but also no download.
          if (err && err.name === 'AbortError') return;
          // Fall through to download fallback if share itself failed.
        }
      }

      // Fallback: trigger a plain download.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke on the next tick so mobile browsers actually finish the fetch.
      setTimeout(() => URL.revokeObjectURL(url), 3000);
      setSaveMsg('Card downloaded. Check your gallery / Downloads folder.');
    } catch (err) {
      console.error('Save card failed:', err);
      setSaveMsg("Couldn't save the card. Try again or use a screenshot.");
    } finally {
      setSavingCard(false);
    }
  };

  const emailHref = useMemo(() => {
    if (!card || !card.email) return null;
    const subject = `Hi ${card.displayName || ''} — scanned your card`;
    const body = message;
    return `mailto:${encodeURIComponent(card.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [card, message]);

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loader}>Loading…</div>
      </div>
    );
  }

  if (notFound || !card) {
    return (
      <div style={styles.page}>
        <div style={styles.cardWrap}>
          <div style={styles.cardEmpty}>
            <div style={styles.brand}>PAR</div>
            <h2 style={styles.notFoundTitle}>Card not found</h2>
            <p style={styles.muted}>
              This business card doesn't exist or hasn't been published yet.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const photoSrc = card.hasPhoto
    ? `${API_URL}/api/nfc-cards/public/${encodeURIComponent(slug)}/photo`
    : null;

  return (
    <div style={styles.page}>
      <div style={styles.cardWrap}>
        <div style={styles.card}>
          <div style={styles.banner} />

          <div style={styles.avatarFrame}>
            {photoSrc ? (
              <img src={photoSrc} alt={card.displayName} style={styles.avatar} />
            ) : (
              <div style={styles.avatarInitials}>
                {(card.displayName || '?').trim().charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          <div style={styles.body}>
            <h1 style={styles.name}>{card.displayName || 'Sales Rep'}</h1>
            {card.headline && <div style={styles.headline}>{card.headline}</div>}
            {card.company && <div style={styles.company}>{card.company}</div>}

            <div style={styles.formSection}>
              <div style={styles.formIntro}>
                Send a quick WhatsApp so {card.displayName ? card.displayName.split(' ')[0] : 'I'} knows who you are.
              </div>

              <div style={styles.formRow}>
                <input
                  type="text"
                  placeholder="First name *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  style={styles.input}
                  maxLength={60}
                />
                <input
                  type="text"
                  placeholder="Surname *"
                  value={form.surname}
                  onChange={(e) => setForm({ ...form, surname: e.target.value })}
                  style={styles.input}
                  maxLength={60}
                />
              </div>
              <input
                type="text"
                placeholder="Company (optional)"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                style={{ ...styles.input, marginTop: '8px' }}
                maxLength={80}
              />
            </div>

            <div style={styles.actions}>
              {whatsappHref ? (
                <a
                  href={canSubmit ? whatsappHref : undefined}
                  onClick={(e) => { if (!canSubmit) e.preventDefault(); }}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    ...styles.waBtn,
                    ...(canSubmit ? {} : styles.btnDisabled)
                  }}
                  aria-disabled={!canSubmit}
                >
                  <span style={styles.waIcon}>💬</span>
                  WhatsApp {card.displayName ? card.displayName.split(' ')[0] : 'me'}
                </a>
              ) : (
                <div style={styles.muted}>WhatsApp number not set.</div>
              )}

              {emailHref && (
                <a
                  href={canSubmit ? emailHref : undefined}
                  onClick={(e) => { if (!canSubmit) e.preventDefault(); }}
                  style={{
                    ...styles.emailBtn,
                    ...(canSubmit ? {} : styles.btnDisabled)
                  }}
                  aria-disabled={!canSubmit}
                >
                  <span style={styles.waIcon}>✉️</span>
                  Email
                </a>
              )}

              <button
                type="button"
                onClick={handleSaveCard}
                disabled={savingCard}
                style={{
                  ...styles.saveBtn,
                  ...(savingCard ? styles.btnDisabled : {})
                }}
              >
                <span style={styles.waIcon}>💾</span>
                {savingCard ? 'Saving…' : 'Save card to my phone'}
              </button>
            </div>

            {!canSubmit && (
              <div style={styles.helperHint}>
                Fill in your first name and surname to send.
              </div>
            )}
            {saveMsg && (
              <div style={styles.saveMsg}>{saveMsg}</div>
            )}
          </div>
        </div>

        <div style={styles.footer}>
          <span style={styles.brandFooter}>PAR</span>
          <span style={styles.muted}>Digital business card</span>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #1E1E1E 0%, #2c2c2c 100%)',
    padding: '24px 16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
  },
  loader: {
    color: 'white',
    padding: '60px',
    textAlign: 'center'
  },
  cardWrap: {
    width: '100%',
    maxWidth: '420px'
  },
  card: {
    background: 'white',
    borderRadius: '18px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
    position: 'relative'
  },
  cardEmpty: {
    background: 'white',
    borderRadius: '18px',
    padding: '40px 20px',
    textAlign: 'center',
    boxShadow: '0 20px 60px rgba(0,0,0,0.35)'
  },
  banner: {
    height: '110px',
    background: 'linear-gradient(135deg, #2fb65c 0%, #1E1E1E 100%)'
  },
  avatarFrame: {
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    background: 'white',
    margin: '-60px auto 0',
    padding: '4px',
    boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
    position: 'relative'
  },
  avatar: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    objectFit: 'cover',
    display: 'block'
  },
  avatarInitials: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    background: '#2fb65c',
    color: 'white',
    fontSize: '44px',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  body: {
    padding: '14px 24px 28px',
    textAlign: 'center'
  },
  name: {
    margin: '8px 0 4px',
    fontSize: '22px',
    color: '#1E1E1E',
    fontWeight: 700
  },
  headline: {
    color: '#555',
    fontSize: '14px',
    marginBottom: '2px'
  },
  company: {
    color: '#777',
    fontSize: '13px',
    marginBottom: '12px'
  },
  formSection: {
    background: '#f7f7f7',
    borderRadius: '12px',
    padding: '14px',
    marginTop: '14px',
    textAlign: 'left'
  },
  formIntro: {
    fontSize: '13px',
    color: '#444',
    marginBottom: '10px'
  },
  formRow: {
    display: 'flex',
    gap: '8px'
  },
  input: {
    flex: 1,
    minWidth: 0,
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '15px',
    boxSizing: 'border-box',
    background: 'white',
    color: '#1E1E1E',
    WebkitTextFillColor: '#1E1E1E'
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginTop: '14px'
  },
  waBtn: {
    background: '#25D366',
    color: 'white',
    padding: '14px 18px',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: 700,
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    boxShadow: '0 4px 12px rgba(37,211,102,0.3)'
  },
  emailBtn: {
    background: 'white',
    color: '#1E1E1E',
    padding: '12px 18px',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: 600,
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    border: '1px solid #ddd'
  },
  saveBtn: {
    background: '#1E1E1E',
    color: 'white',
    padding: '12px 18px',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  saveMsg: {
    marginTop: '10px',
    fontSize: '13px',
    color: '#2fb65c',
    textAlign: 'center'
  },
  waIcon: {
    fontSize: '18px'
  },
  btnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'auto'  // keep onClick guard responsive on mobile
  },
  helperHint: {
    color: '#999',
    fontSize: '12px',
    marginTop: '10px'
  },
  footer: {
    textAlign: 'center',
    padding: '14px',
    color: 'rgba(255,255,255,0.7)',
    fontSize: '12px',
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    alignItems: 'center'
  },
  brand: {
    color: '#2fb65c',
    fontWeight: 800,
    fontSize: '18px',
    letterSpacing: '0.1em',
    marginBottom: '10px'
  },
  brandFooter: {
    color: '#2fb65c',
    fontWeight: 700,
    letterSpacing: '0.1em'
  },
  notFoundTitle: {
    color: '#1E1E1E',
    margin: '8px 0'
  },
  muted: {
    color: '#888',
    fontSize: '13px'
  }
};

export default PublicNfcCard;
